/**
 * Language selector.
 *
 * A searchable combobox rather than a `<select>`, because a list of 40+
 * languages in a native select on a phone is unusable. Full keyboard support:
 * arrows to move, Enter to choose, Escape to close, type to filter.
 */

import { useEffect, useId, useMemo, useRef, useState } from "react";
import type { LanguageCode, LanguageOption } from "@shared/types/language";
import { languageOptions, languageLabel, languageShortLabel } from "@shared/languages";
import { IconCheck, IconDetect, IconGlobe } from "./icons";

interface Props {
  value: LanguageCode | "auto";
  onChange: (value: LanguageCode | "auto") => void;
  label: string;
  includeAuto?: boolean;
  disabled?: boolean;
  /** Show the endonym under the English name. */
  showNative?: boolean;
}

export function LanguagePicker({ value, onChange, label, includeAuto = false, disabled = false, showNative = true }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  const options = useMemo(() => languageOptions(includeAuto), [includeAuto]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return options;
    return options.filter(
      (option) => option.label.toLowerCase().includes(needle) || option.nativeLabel.toLowerCase().includes(needle) || option.code.toLowerCase().includes(needle),
    );
  }, [options, query]);

  const selected = options.find((option) => option.code === value) ?? options[0];

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent): void => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setHighlight(Math.max(0, filtered.findIndex((option) => option.code === value)));
      // Focus after paint so the popover is already laid out.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
    // Re-running on `filtered` would reset the query mid-typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const commit = (option: LanguageOption | undefined): void => {
    if (!option) return;
    onChange(option.code as LanguageCode | "auto");
    setOpen(false);
  };

  return (
    <div className="field" ref={rootRef} style={{ position: "relative" }}>
      <span className="field__label">{label}</span>
      <button
        type="button"
        className="input"
        onClick={() => setOpen((current) => !current)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        style={{ display: "flex", alignItems: "center", gap: "var(--space-2xs)", textAlign: "left", cursor: disabled ? "not-allowed" : "pointer" }}
      >
        {value === "auto" ? <IconDetect size={15} className="dim" /> : <IconGlobe size={15} className="dim" />}
        <span className="grow truncate" style={{ fontWeight: 550 }}>
          {value === "auto" ? "Detect language" : (selected?.nativeLabel ?? value)}
        </span>
        <span className="dim truncate" style={{ fontSize: "var(--step--2)", maxWidth: "9ch" }}>
          {value === "auto" ? "auto" : selected?.label}
        </span>
      </button>

      {open ? (
        <div
          className="panel panel--glass"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            marginTop: "var(--space-3xs)",
            zIndex: 30,
            maxHeight: 300,
            overflowY: "auto",
            boxShadow: "var(--shadow-l)",
          }}
        >
          <div style={{ padding: "var(--space-2xs)", position: "sticky", top: 0, background: "var(--glass)", backdropFilter: "var(--blur)" }}>
            <input
              ref={inputRef}
              className="input"
              value={query}
              placeholder="Search languages…"
              onChange={(event) => {
                setQuery(event.target.value);
                setHighlight(0);
              }}
              onKeyDown={(event) => {
                if (event.key === "ArrowDown") {
                  event.preventDefault();
                  setHighlight((current) => Math.min(filtered.length - 1, current + 1));
                } else if (event.key === "ArrowUp") {
                  event.preventDefault();
                  setHighlight((current) => Math.max(0, current - 1));
                } else if (event.key === "Enter") {
                  event.preventDefault();
                  commit(filtered[highlight]);
                } else if (event.key === "Escape") {
                  event.preventDefault();
                  setOpen(false);
                }
              }}
            />
          </div>
          <ul id={listId} role="listbox" style={{ listStyle: "none", margin: 0, padding: "var(--space-3xs)" }}>
            {filtered.length === 0 ? (
              <li className="dim" style={{ padding: "var(--space-2xs) var(--space-s)", fontSize: "var(--step--1)" }}>
                No language matches “{query}”.
              </li>
            ) : null}
            {filtered.map((option, index) => {
              const isSelected = option.code === value;
              return (
                <li key={option.code}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onMouseEnter={() => setHighlight(index)}
                    onClick={() => commit(option)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "var(--space-2xs)",
                      width: "100%",
                      padding: "var(--space-3xs) var(--space-2xs)",
                      borderRadius: "var(--radius-xs)",
                      background: index === highlight ? "var(--bg-panel-hover)" : "transparent",
                      textAlign: "left",
                    }}
                  >
                    <span className="grow truncate">
                      {showNative ? option.nativeLabel : option.label}
                      <span className="dim" style={{ fontSize: "var(--step--2)", marginLeft: "var(--space-2xs)" }}>
                        {showNative ? option.label : option.code}
                      </span>
                    </span>
                    {isSelected ? <IconCheck size={14} style={{ color: "var(--accent)" }} /> : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

/** Compact read-only label used in headers and transcript rows. */
export function LanguageChip({ code, tone = "default" }: { code: LanguageCode | "auto"; tone?: "default" | "accent" }) {
  if (code === "auto") {
    return (
      <span className={`badge${tone === "accent" ? " badge--accent" : ""}`}>
        <IconDetect size={12} /> Auto
      </span>
    );
  }
  return (
    <span className={`badge${tone === "accent" ? " badge--accent" : ""}`} title={languageLabel(code)}>
      {languageShortLabel(code)}
    </span>
  );
}
